import { useEffect, useState, useRef } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { SettingsPageHeader } from '@/components/settings/SettingsPageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Building2, Palette, FileText, Save, RefreshCw, Eye, Upload, Image, Download, Printer, ExternalLink, CheckCircle2, XCircle, Activity, RotateCcw, Copy } from 'lucide-react';
import api from '@/lib/api';
import { resolveUploadUrl } from '@/lib/uploadUrl';
import { SSoTCompanyInfoBanner } from '@/components/common/SSoTCompanyInfoBanner';
import { toast } from 'sonner';

/**
 * iter686 — One-click sanity check that the thermal logo URL is fully
 * reachable from the browser with the CORS posture that eposPrint.js
 * requires for printing. Catches the 90% case of "logo not printing":
 *   • file 404 (wrong URL after upload)
 *   • cross-origin without `Access-Control-Allow-Origin`
 *   • image too large (decode failure on Safari)
 *   • slow CDN (> 4s = likely to time out at print time too)
 *
 * Renders a small chip-shaped button. Click → runs the probe → shows
 * a green or red badge with the exact failure reason.
 */
function LogoCorsHealthCheck({ url }) {
  const [state, setState] = useState({ status: 'idle', detail: '', ms: 0 });

  const probe = async () => {
    if (!url) return;
    setState({ status: 'running', detail: 'fetching…', ms: 0 });
    const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    try {
      // 1. Fetch via Image() with crossOrigin='anonymous' — exactly
      //    what the print path does. A failure here = same failure
      //    the printer will see.
      await new Promise((resolve, reject) => {
        const img = new globalThis.Image();
        img.crossOrigin = 'anonymous';
        const timer = setTimeout(() => reject(new Error('timeout (4s)')), 4000);
        img.onload = () => { clearTimeout(timer); resolve(img); };
        img.onerror = () => { clearTimeout(timer); reject(new Error('load failed (404 or CORS blocked)')); };
        img.src = url;
      });

      // 2. Canvas read-back. If the image loaded but the origin
      //    doesn't return `Access-Control-Allow-Origin`, this throws
      //    `SecurityError: Tainted canvases may not be exported`.
      const img2 = new globalThis.Image();
      img2.crossOrigin = 'anonymous';
      await new Promise((res, rej) => { img2.onload = res; img2.onerror = rej; img2.src = url; });
      const canvas = document.createElement('canvas');
      canvas.width = img2.naturalWidth || 1;
      canvas.height = img2.naturalHeight || 1;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img2, 0, 0);
      ctx.getImageData(0, 0, 1, 1); // throws on taint
      const ms = Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0);
      setState({ status: 'ok', detail: `${img2.naturalWidth}×${img2.naturalHeight}px in ${ms}ms`, ms });
      toast.success(`Logo reachable → ${img2.naturalWidth}×${img2.naturalHeight}px in ${ms}ms. It will print.`);
    } catch (e) {
      const ms = Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0);
      const msg = (e && e.message) || String(e);
      let hint = msg;
      if (/tainted/i.test(msg) || /security/i.test(msg)) {
        hint = 'Server returned the image but DID NOT send `Access-Control-Allow-Origin`. Move the logo to smartoerp.com OR add that header on your CDN.';
      } else if (/timeout/i.test(msg)) {
        hint = 'Server took > 4s to respond. iPad print will silently skip the logo at this speed.';
      } else if (/load failed/i.test(msg)) {
        hint = 'Browser couldn\u2019t fetch the URL. Common cause: file 404 (broken path) or HTTPS cert error.';
      }
      setState({ status: 'fail', detail: hint, ms });
      toast.error(`Logo fails CORS check: ${hint}`);
    }
  };

  const chipClass = state.status === 'ok' ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
    : state.status === 'fail' ? 'bg-rose-100 text-rose-800 border-rose-300'
    : state.status === 'running' ? 'bg-sky-100 text-sky-800 border-sky-300'
    : 'bg-stone-100 text-stone-700 border-stone-300 hover:bg-stone-200';

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button
        type="button"
        onClick={probe}
        disabled={state.status === 'running'}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${chipClass}`}
        data-testid="logo-cors-health-check-btn"
        title="Verify the iPad will be able to fetch this logo for thermal printing"
      >
        {state.status === 'running' ? <RefreshCw size={10} className="animate-spin" />
          : state.status === 'ok' ? <CheckCircle2 size={10} />
          : state.status === 'fail' ? <XCircle size={10} />
          : <Activity size={10} />}
        {state.status === 'idle' ? 'CORS health check' : state.status === 'running' ? 'Checking…' : state.status === 'ok' ? `OK (${state.ms}ms)` : 'Failed'}
      </button>
      {state.status === 'fail' && (
        <span className="text-[10px] text-rose-600 max-w-md leading-snug" data-testid="logo-cors-health-detail-fail">
          {state.detail}
        </span>
      )}
      {state.status === 'ok' && (
        <span className="text-[10px] text-emerald-700" data-testid="logo-cors-health-detail-ok">
          {state.detail}
        </span>
      )}
    </div>
  );
}


// iter629 — Inline editor for per-branch thermal receipt header/footer.
// Lives inside the Branding Settings page so admins manage all
// brand-facing text in one place, instead of inside the POS dialog.
//
// iter685 — Now CASCADE-aware. Each per-branch field shows whether it
// is currently INHERITED from the tenant-wide default (Settings →
// Receipt Settings) or OVERRIDDEN with a branch-specific value. Two
// affordances:
//   • An "Inherited" badge + ghosted placeholder = no override.
//   • "Reset to default" button = clear branch value → falls back to
//     tenant default automatically.
function BranchReceiptCard({ branch, tenantDefaults, onSaved }) {
  const [header, setHeader] = useState(branch?.receipt_header || '');
  const [footer, setFooter] = useState(branch?.receipt_footer || '');
  const [cPhone, setCPhone] = useState(branch?.company_phone || '');
  const [cAddress, setCAddress] = useState(branch?.company_address || '');
  const [cEmail, setCEmail] = useState(branch?.company_email || '');
  const [cVat, setCVat] = useState(branch?.company_vat || '');
  const [saving, setSaving] = useState(false);

  // iter685 — Helpful flags for UX. A field is "inherited" when the
  // branch-level value is blank: the receipt builder falls back to
  // tenantDefaults automatically (see posPrint.js cascade).
  const headerInherited = !header.trim();
  const footerInherited = !footer.trim();
  const tenantHeader = (tenantDefaults?.receipt_header_text || '').trim();
  const tenantFooter = (tenantDefaults?.footer_text || 'Thank you!').trim();
  // iter775 — master Company Profile values shown as the inherited placeholder.
  const mPhone = (tenantDefaults?.company_phone || '').trim();
  const mAddress = (tenantDefaults?.company_address || '').trim();
  const mEmail = (tenantDefaults?.company_email || '').trim();
  const mVat = (tenantDefaults?.company_vat || '').trim();

  const save = async () => {
    if (header.length > 200 || footer.length > 200) {
      toast.error('Header / footer max 200 chars each');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/branches/${branch.id}/receipt-settings`,
        {
          receipt_header: header.trim(),
          receipt_footer: footer.trim(),
          company_phone: cPhone.trim(),
          company_address: cAddress.trim(),
          company_email: cEmail.trim(),
          company_vat: cVat.trim(),
        });
      toast.success(`${branch.name || 'Branch'}: settings saved`);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  // iter777 — one-click prefill from the master Company Profile.
  const copyFromProfile = () => {
    setCPhone(mPhone); setCAddress(mAddress); setCEmail(mEmail); setCVat(mVat);
    toast.success('Filled from Company Profile — tweak any field, then Save');
  };
  // iter777 — flag whether the receipt company block prints this branch's
  // own value or inherits the master profile, per field.
  const srcTag = (v) => (String(v).trim()
    ? <span className="ml-1 text-[9px] px-1 py-px rounded bg-amber-100 text-amber-700 font-semibold align-middle">Branch</span>
    : <span className="ml-1 text-[9px] px-1 py-px rounded bg-stone-100 text-stone-500 align-middle">Company</span>);

  return (
    <div className="p-3 rounded-lg border border-stone-200 bg-white space-y-2"
         data-testid={`branch-receipt-card-${branch.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="font-semibold text-sm text-stone-800 truncate">{branch.name || 'Branch'}</div>
        {headerInherited && footerInherited && (
          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200 font-medium"
                data-testid={`branch-fully-inherited-${branch.id}`}>
            All inherited
          </span>
        )}
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-[10px] text-stone-600">Header line (top of receipt)</Label>
            <div className="flex items-center gap-1">
              {headerInherited ? (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200"
                      title="Currently using the company-wide default. Type something here to override just this branch.">
                  Inherited
                </span>
              ) : (
                <button type="button"
                        onClick={() => setHeader('')}
                        className="text-[9px] px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-700 border border-zinc-300 hover:bg-zinc-200"
                        title="Clear → fall back to the company default"
                        data-testid={`branch-header-reset-${branch.id}`}>
                  Reset to default
                </button>
              )}
            </div>
          </div>
          <Input value={header} onChange={(e) => setHeader(e.target.value)}
                 placeholder={tenantHeader ? `(inherited) ${tenantHeader.slice(0, 40)}${tenantHeader.length > 40 ? '…' : ''}` : 'e.g., Welcome to Cafe XYZ'}
                 maxLength={200} className="h-8 text-[12px]"
                 data-testid={`branch-header-${branch.id}`} />
          <div className="text-[9px] text-stone-400 text-right mt-0.5">{header.length}/200</div>
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-[10px] text-stone-600">Footer line (bottom of receipt)</Label>
            <div className="flex items-center gap-1">
              {footerInherited ? (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200"
                      title="Currently using the company-wide default. Type something here to override just this branch.">
                  Inherited
                </span>
              ) : (
                <button type="button"
                        onClick={() => setFooter('')}
                        className="text-[9px] px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-700 border border-zinc-300 hover:bg-zinc-200"
                        title="Clear → fall back to the company default"
                        data-testid={`branch-footer-reset-${branch.id}`}>
                  Reset to default
                </button>
              )}
            </div>
          </div>
          <Input value={footer} onChange={(e) => setFooter(e.target.value)}
                 placeholder={tenantFooter ? `(inherited) ${tenantFooter.slice(0, 40)}${tenantFooter.length > 40 ? '…' : ''}` : 'e.g., Thank you — @cafexyz'}
                 maxLength={200} className="h-8 text-[12px]"
                 data-testid={`branch-footer-${branch.id}`} />
          <div className="text-[9px] text-stone-400 text-right mt-0.5">{footer.length}/200</div>
        </div>
      </div>
      {/* iter775 — Optional per-branch company-contact overrides */}
      <div className="pt-2 mt-1 border-t border-stone-100">
        <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
          <div className="text-[10px] font-semibold text-stone-600">
            Branch contact overrides <span className="font-normal text-stone-400">(leave blank to inherit company profile)</span>
          </div>
          <button type="button" onClick={copyFromProfile}
                  className="text-[10px] font-medium text-emerald-700 hover:text-emerald-800 hover:underline inline-flex items-center gap-0.5"
                  data-testid={`branch-copy-profile-${branch.id}`}>
            <Copy size={10} /> Copy from company profile
          </button>
        </div>
        <p className="text-[9px] text-stone-400 mb-1.5" data-testid={`branch-source-legend-${branch.id}`}>
          The <strong>Branch</strong> tag = this branch's own value prints on its receipts; <strong>Company</strong> = inherits the master profile.
        </p>
        <div className="grid sm:grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] text-stone-600">Phone {srcTag(cPhone)}</Label>
            <Input value={cPhone} onChange={(e) => setCPhone(e.target.value)}
                   placeholder={mPhone ? `(company) ${mPhone}` : 'Branch phone'}
                   maxLength={60} className="h-8 text-[12px]"
                   data-testid={`branch-phone-${branch.id}`} />
          </div>
          <div>
            <Label className="text-[10px] text-stone-600">VAT number {srcTag(cVat)}</Label>
            <Input value={cVat} onChange={(e) => setCVat(e.target.value)}
                   placeholder={mVat ? `(company) ${mVat}` : 'Branch VAT'}
                   maxLength={60} className="h-8 text-[12px]"
                   data-testid={`branch-vat-${branch.id}`} />
          </div>
          <div>
            <Label className="text-[10px] text-stone-600">Email {srcTag(cEmail)}</Label>
            <Input value={cEmail} onChange={(e) => setCEmail(e.target.value)}
                   placeholder={mEmail ? `(company) ${mEmail}` : 'Branch email'}
                   maxLength={120} className="h-8 text-[12px]"
                   data-testid={`branch-email-${branch.id}`} />
          </div>
          <div>
            <Label className="text-[10px] text-stone-600">Address {srcTag(cAddress)}</Label>
            <Input value={cAddress} onChange={(e) => setCAddress(e.target.value)}
                   placeholder={mAddress ? `(company) ${mAddress.slice(0, 30)}${mAddress.length > 30 ? '…' : ''}` : 'Branch address'}
                   maxLength={300} className="h-8 text-[12px]"
                   data-testid={`branch-address-${branch.id}`} />
          </div>
        </div>
      </div>
      <div className="flex justify-end">
        <Button size="sm" onClick={save} disabled={saving}
                className="h-7 text-[11px] bg-stone-700 hover:bg-stone-800 text-white"
                data-testid={`branch-receipt-save-${branch.id}`}>
          {saving ? <RefreshCw size={11} className="animate-spin mr-1" /> : <Save size={11} className="mr-1" />}
          Save
        </Button>
      </div>
    </div>
  );
}

export default function BrandingSettingsPage() {
  const [branding, setBranding] = useState({
    company_name: 'Smarto',
    company_address: '',
    company_phone: '',
    company_email: '',
    company_vat: '',
    logo_url: '',
    thermal_logo_url: '',
    thermal_logo_size: 'medium',
    primary_color: '#10B981',
    footer_text: 'Thank you for your business!',
  });
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingThermal, setUploadingThermal] = useState(false);
  const fileInputRef = useRef(null);
  const thermalFileInputRef = useRef(null);

  useEffect(() => {
    fetchBranding();
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const { data } = await api.get('/branches');
      setBranches(Array.isArray(data) ? data : []);
    } catch { setBranches([]); }
  };

  const fetchBranding = async () => {
    try {
      const res = await api.get('/pdf-exports/branding');
      setBranding(prev => ({ ...prev, ...res.data }));
    } catch (error) {
      // Use defaults
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/pdf-exports/branding', branding);
      toast.success('Branding settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/pdf-exports/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setBranding(prev => ({ ...prev, logo_url: res.data.logo_url }));
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  // iter629 — Separate logo upload for thermal receipts. Stored as
  // tenant-wide URL on the branding doc. The Print Bridge fetches +
  // downscales to 384px monochrome before sending ESC/POS bit-image.
  const handleThermalLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    setUploadingThermal(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      // iter715 Phase D — Use the dedicated /branding/upload-thermal-logo
      // endpoint so the upload only touches `thermal_logo_url` and leaves
      // `logo_url` (the colour logo for PDFs / sign-in screen) alone.
      const res = await api.post('/branding/upload-thermal-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setBranding(prev => ({ ...prev, thermal_logo_url: res.data.thermal_logo_url || res.data.logo_url }));
      toast.success('Thermal receipt logo uploaded. Click Save to persist.');
    } catch (error) {
      toast.error('Failed to upload thermal logo');
    } finally {
      setUploadingThermal(false);
    }
  };

  const handlePreview = async () => {
    try {
      const res = await api.post('/pdf-exports/generate', {
        report_type: 'pnl',
        title: 'Sample P&L Report',
        include_logo: true,
        include_footer: true,
      }, { responseType: 'blob' });
      
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Failed to generate preview');
    }
  };

  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="animate-spin text-success" size={32} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-3xl mx-auto">
        <SettingsPageHeader
          icon={Palette}
          title="Branding Settings"
          subtitle="Customize your company branding for PDF exports and reports."
          accent="rose"
          titleTestId="branding-title"
        />

        {/* Live Preview Card */}
        <Card className="border-success/30 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {branding.logo_url ? (
                  <img 
                    src={resolveUploadUrl(branding.logo_url)} 
                    alt="Logo" 
                    className="w-14 h-14 rounded-lg object-contain bg-white border shadow-sm"
                    data-testid="branding-logo-preview"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-lg bg-white/70 border border-dashed border-border flex items-center justify-center">
                    <Image size={20} className="text-muted-foreground" />
                  </div>
                )}
                <div>
                  <h2 className="text-2xl font-bold" style={{ color: branding.primary_color }}>
                    {branding.company_name}
                  </h2>
                  <p className="text-sm text-muted-foreground dark:text-muted-foreground">{branding.company_address || 'Company Address'}</p>
                  <p className="text-xs text-muted-foreground">
                    {[branding.company_phone, branding.company_email, branding.company_vat && `VAT: ${branding.company_vat}`]
                      .filter(Boolean)
                      .join(' | ') || 'Contact Info'}
                  </p>
                </div>
              </div>
              <Button variant="outline" onClick={handlePreview} data-testid="preview-pdf-btn">
                <Eye size={14} className="mr-1" /> Preview PDF
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Logo Upload */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Upload size={18} /> Company Logo
            </CardTitle>
            <CardDescription>Upload your company logo for PDF report headers</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              {branding.logo_url ? (
                <img 
                  src={resolveUploadUrl(branding.logo_url)} 
                  alt="Current logo" 
                  className="w-20 h-20 rounded-xl object-contain bg-white border shadow-sm"
                />
              ) : (
                <div className="w-20 h-20 rounded-xl bg-muted border-2 border-dashed border-border flex items-center justify-center">
                  <Image size={28} className="text-muted-foreground" />
                </div>
              )}
              <div className="space-y-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  data-testid="logo-file-input"
                />
                <Button 
                  variant="outline" 
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="upload-logo-btn"
                >
                  {uploading ? <RefreshCw className="animate-spin mr-1" size={14} /> : <Upload size={14} className="mr-1" />}
                  {uploading ? 'Uploading...' : branding.logo_url ? 'Change Logo' : 'Upload Logo'}
                </Button>
                {branding.logo_url && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setBranding(prev => ({ ...prev, logo_url: '' })); toast.success('Logo reset to default — click Save to apply'); }}
                    data-testid="reset-logo-btn"
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <RotateCcw size={14} className="mr-1" /> Reset to default
                  </Button>
                )}
                <p className="text-xs text-muted-foreground">PNG, JPG or SVG. Max 2MB recommended.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Company Info — now sourced from the Company Profile (SSoT) */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 size={18} /> Company Information
            </CardTitle>
            <CardDescription>Company name, VAT, CR &amp; contact details are managed once in your Company Profile and reused on every PDF, invoice and receipt.</CardDescription>
          </CardHeader>
          <CardContent>
            <SSoTCompanyInfoBanner
              editHref="/settings/company-profile"
              scopeLabel="Company info"
              previewFields={['company_name', 'company_vat', 'cr_number']}
              data-testid="branding-company-ssot"
            />
          </CardContent>
        </Card>

        {/* Appearance */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Palette size={18} /> Appearance
            </CardTitle>
            <CardDescription>Customize the look of your exports</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="primary_color">Primary Color</Label>
              <div className="flex gap-2">
                <Input
                  id="primary_color"
                  type="color"
                  value={branding.primary_color}
                  onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                  className="w-16 h-10 p-1 cursor-pointer"
                />
                <Input
                  value={branding.primary_color}
                  onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                  placeholder="#10B981"
                  className="flex-1"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText size={18} /> Footer
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="footer_text">Footer Text</Label>
              <Input
                id="footer_text"
                value={branding.footer_text}
                onChange={(e) => setBranding({ ...branding, footer_text: e.target.value })}
                placeholder="Thank you for your business!"
              />
            </div>
          </CardContent>
        </Card>

        {/* Brand Assets - Download Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-outfit"><Image size={20} className="text-orange-500" /> Brand Assets</CardTitle>
            <CardDescription>Download Smarto logos for marketing, print, and digital use</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Full Logo (Dark) */}
              <div className="flex flex-col items-center gap-3 p-5 rounded-xl border border-border bg-white" data-testid="brand-asset-main">
                <div className="h-28 flex items-center justify-center">
                  <img src="/smarto-logo-main.png" alt="Smarto Logo" className="max-h-full max-w-full object-contain" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold text-foreground">Full Logo</p>
                  <p className="text-xs text-muted-foreground">For light backgrounds</p>
                </div>
                <a href="/smarto-logo-main.png" download="Smarto-Logo.png" className="w-full">
                  <Button variant="outline" className="w-full rounded-lg gap-2 text-sm">
                    <Download size={14} /> Download PNG
                  </Button>
                </a>
              </div>

              {/* S Icon Only */}
              <div className="flex flex-col items-center gap-3 p-5 rounded-xl border border-border bg-white" data-testid="brand-asset-icon">
                <div className="h-28 flex items-center justify-center">
                  <img src="/smarto-icon.png" alt="Smarto Icon" className="max-h-full max-w-full object-contain" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold text-foreground">S Icon</p>
                  <p className="text-xs text-muted-foreground">App icon &amp; favicon</p>
                </div>
                <a href="/smarto-icon.png" download="Smarto-Icon.png" className="w-full">
                  <Button variant="outline" className="w-full rounded-lg gap-2 text-sm">
                    <Download size={14} /> Download PNG
                  </Button>
                </a>
              </div>

              {/* White Logo (for dark/orange BG) */}
              <div className="flex flex-col items-center gap-3 p-5 rounded-xl border border-border bg-gradient-to-br from-orange-500 to-amber-500" data-testid="brand-asset-white">
                <div className="h-28 flex items-center justify-center">
                  <div className="flex items-center gap-2">
                    <img src="/smarto-icon.png" alt="Smarto" className="h-12 w-12 object-contain" />
                    <span className="text-xl font-bold font-outfit tracking-wider text-white">SMARTO</span>
                  </div>
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold text-white">On Dark / Orange</p>
                  <p className="text-xs text-white/70">For colored backgrounds</p>
                </div>
                <a href="/smarto-icon.png" download="Smarto-Icon-For-Dark-BG.png" className="w-full">
                  <Button variant="outline" className="w-full rounded-lg gap-2 text-sm bg-white/20 border-white/30 text-white hover:bg-white/30">
                    <Download size={14} /> Download Icon
                  </Button>
                </a>
              </div>
            </div>

            <div className="mt-4 p-3 bg-muted/50 rounded-lg border border-border">
              <p className="text-xs text-muted-foreground"><span className="font-semibold">Brand Colors:</span> Dark Orange (#D04520) &middot; Bright Orange (#F58020) &middot; Golden Amber (#F5B020)</p>
            </div>
          </CardContent>
        </Card>

        {/* iter629 — Thermal Receipt Branding (separate from PDF branding). */}
        <Card data-testid="thermal-branding-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Printer size={18} className="text-amber-700" />
              Thermal Receipt Branding
            </CardTitle>
            <CardDescription>
              Customise what prints on every thermal receipt — a logo, a per-branch header line (welcome / address), and a per-branch footer line (visit again / social handle).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Thermal logo uploader (tenant-wide) */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Receipt logo (small, monochrome)</Label>
              <div className="flex items-start gap-4 flex-wrap">
                <div className="w-24 h-24 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50/40 flex items-center justify-center overflow-hidden">
                  {branding.thermal_logo_url ? (
                    <img src={resolveUploadUrl(branding.thermal_logo_url)} alt="Thermal logo" className="max-w-full max-h-full object-contain" data-testid="thermal-logo-preview" />
                  ) : (
                    <Image size={28} className="text-amber-400" />
                  )}
                </div>
                <div className="flex-1 min-w-[200px] space-y-1">
                  <input
                    type="file"
                    accept="image/png,image/jpeg"
                    ref={thermalFileInputRef}
                    onChange={handleThermalLogoUpload}
                    className="hidden"
                    data-testid="thermal-logo-file-input"
                  />
                  <Button variant="outline" onClick={() => thermalFileInputRef.current?.click()}
                          disabled={uploadingThermal} data-testid="thermal-logo-upload-btn">
                    {uploadingThermal ? <RefreshCw size={14} className="animate-spin mr-1" /> : <Upload size={14} className="mr-1" />}
                    {branding.thermal_logo_url ? 'Change' : 'Upload'} thermal logo
                  </Button>
                  <p className="text-[11px] text-muted-foreground">
                    PNG or JPG. Auto-converted to monochrome and sized per the Logo Print Size below before printing. Upload a crisp, high-resolution logo for best results.
                  </p>
                  {branding.thermal_logo_url && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button variant="ghost" size="sm"
                              onClick={() => setBranding(prev => ({ ...prev, thermal_logo_url: '' }))}
                              className="text-[10px] h-6 text-rose-600"
                              data-testid="thermal-logo-remove-btn">
                        Remove logo
                      </Button>
                      {/* iter686 — CORS health check. Verifies the logo
                          is reachable from the operator's browser with
                          crossOrigin="anonymous" — exactly the same way
                          eposPrint.js loads it for printing. If this
                          probe succeeds, the iPad WILL render the logo
                          on receipts. If it fails, we surface the
                          actual error so the operator knows whether to
                          move the file to smartoerp.com or add CORS
                          headers on their CDN. */}
                      <LogoCorsHealthCheck
                        url={resolveUploadUrl(branding.thermal_logo_url)}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* iter767 — Logo print size on receipts */}
              <div className="space-y-1.5 pt-1" data-testid="thermal-logo-size">
                <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Logo print size</Label>
                <div className="flex gap-2">
                  {[
                    { v: 'small', label: 'Small' },
                    { v: 'medium', label: 'Medium' },
                    { v: 'large', label: 'Large' },
                  ].map(opt => (
                    <Button
                      key={opt.v}
                      type="button"
                      variant={(branding.thermal_logo_size || 'medium') === opt.v ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setBranding(prev => ({ ...prev, thermal_logo_size: opt.v }))}
                      data-testid={`thermal-logo-size-${opt.v}`}
                      className="flex-1"
                    >
                      {opt.label}
                    </Button>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground">Controls how wide the logo prints on thermal receipts. Save, then print a test receipt to check.</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold flex items-center gap-2">
                Per-branch header & footer text
                <a href="/printer-setup" target="_blank" rel="noreferrer"
                   className="text-[11px] text-indigo-600 hover:underline font-normal flex items-center gap-1">
                  Printer setup guide <ExternalLink size={10} />
                </a>
              </Label>
              <p className="text-[11px] text-muted-foreground">
                Each branch can stamp its own welcome line at the top of the receipt and a thank-you / promo line at the bottom. Changes save instantly.
              </p>
              {branches.length === 0 ? (
                <div className="text-[12px] text-muted-foreground italic py-4 text-center">
                  No branches configured yet.
                </div>
              ) : (
                <div className="space-y-2" data-testid="branch-receipt-list">
                  {/* iter685 — Tiny cascade legend so admins instantly
                      grok the inheritance model the first time they
                      land on this page. */}
                  <div className="text-[10px] text-stone-500 bg-stone-50 border border-stone-200 rounded-md px-2 py-1.5 mb-1 flex items-center gap-2 flex-wrap">
                    <span>How it works:</span>
                    <span className="px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200">Inherited</span>
                    <span>= using company default.</span>
                    <span>Type to override.</span>
                    <span className="px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-700 border border-zinc-300">Reset to default</span>
                    <span>= back to inheriting.</span>
                  </div>
                  {branches.map(b => (
                    <BranchReceiptCard
                      key={b.id}
                      branch={b}
                      tenantDefaults={branding}
                      onSaved={fetchBranches}
                    />
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={fetchBranding}>
            <RefreshCw size={14} className="mr-1" /> Reset
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-success hover:bg-emerald-700" data-testid="save-branding-btn">
            {saving ? <RefreshCw className="animate-spin mr-1" size={14} /> : <Save size={14} className="mr-1" />}
            Save Settings
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
